import { type QueryObserverResult, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { getAvailableTaskPoints } from './task-points-client';
import type { AvailableTaskPointsResponse, UseAvailableTaskPointsInput } from './task-points-client.type';

/**
 * Query manager for the distinct story-point estimates available within a
 * single entity scope. The query stays disabled until `entityId` resolves to
 * a non-null value, so callers can render a default ("All story points")
 * without juggling enablement themselves.
 *
 * @param {UseAvailableTaskPointsInput} input - Entity scope; `entityId` may be null while the active entity is still resolving
 * @param {UseQueryOptions<AvailableTaskPointsResponse>} options - Additional React Query options
 * @returns {{ points: number[]; hasUnestimated: boolean; query: QueryObserverResult<AvailableTaskPointsResponse> }} The numeric points, whether unestimated tasks exist, and the query result
 */
const useAvailableTaskPoints = (
  input: UseAvailableTaskPointsInput,
  options?: Omit<UseQueryOptions<AvailableTaskPointsResponse>, 'queryKey' | 'queryFn' | 'enabled'>,
): {
  points: number[];
  hasUnestimated: boolean;
  query: QueryObserverResult<AvailableTaskPointsResponse>;
} => {
  const { organizationId, entityType, entityId, startDate, endDate } = input;

  const query = useQuery({
    queryKey: ['available-task-points', organizationId, entityType, entityId, startDate ?? '', endDate ?? ''],
    queryFn: ({ signal }) => {
      if (!organizationId || !entityId) {
        return Promise.reject(new Error('Organization id and entity id are required'));
      }
      return getAvailableTaskPoints({ organizationId, entityType, entityId, startDate, endDate }, signal);
    },
    enabled: Boolean(organizationId && entityId),
    ...options,
  });

  return {
    points: query.data?.points ?? [],
    hasUnestimated: query.data?.has_unestimated ?? false,
    query,
  };
};

export { useAvailableTaskPoints };
